import { Controller } from '@hotwired/stimulus';

// A small, self-contained searchable dropdown for the booking form's category / studio <select>s.
// Purpose-built rather than reusing the shared `autocomplete` controller so it can, without
// touching the rest of the app:
//   - ALWAYS expose every option when opened, even with a value already selected (the shared
//     accessible-autocomplete widget collapses to just the selected option after a reload),
//   - fire a native `change` on the underlying <select> only when the value actually changes,
//     which drives the autosave + progressive-reveal controllers without spurious re-renders.
//
// The real <select> is kept in the DOM (hidden) as the source of truth and the submitted field;
// this controller renders an <input> + listbox on top of it, reusing the .autocomplete__* styles.
export default class extends Controller {
  connect() {
    if (this.element.tagName !== 'SELECT') return;

    this.select = this.element;
    this.options = Array.from(this.select.options)
      .filter((option) => option.value !== '')
      .map((option) => ({ value: option.value, label: (option.textContent || '').trim() }));
    const placeholderOption = Array.from(this.select.options).find((option) => option.value === '');
    this.placeholder = placeholderOption ? (placeholderOption.textContent || '').trim() : '';

    this.buildWidget();

    // Hide the native select but keep it submitting; move its id onto the visible input.
    const id = this.select.id;
    if (id) {
      this.select.id = `${id}-select`;
      this.input.id = id;
    }
    this.select.hidden = true;
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');
  }

  disconnect() {
    this.cancelClose();
    if (this.wrapper) this.wrapper.remove();
    if (!this.select) return;
    if (this.input && this.input.id) {
      this.select.id = this.input.id;
    }
    this.select.hidden = false;
    this.select.removeAttribute('tabindex');
    this.select.removeAttribute('aria-hidden');
  }

  buildWidget() {
    this.activeIndex = -1;

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'autocomplete__wrapper';

    this.input = document.createElement('input');
    this.input.type = 'text';
    // booking-select__input gets the dropdown chevron (see _forms.scss) without pulling in
    // .autocomplete__input, whose transparent-placeholder rule would hide our prompt text.
    this.input.className = 'booking-form__input booking-select__input';
    this.input.autocomplete = 'off';
    this.input.setAttribute('role', 'combobox');
    this.input.placeholder = this.placeholder;
    this.input.value = this.selectedLabel();

    this.menu = document.createElement('ul');
    this.menu.className = 'autocomplete__menu autocomplete__menu--hidden';
    this.menu.dataset.placement = 'bottom';
    this.menu.setAttribute('role', 'listbox');

    this.wrapper.append(this.input, this.menu);
    this.select.parentNode.insertBefore(this.wrapper, this.select);

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('click', () => this.open());
    this.input.addEventListener('input', () => this.open(this.input.value));
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    // Delay so a click on an option (which blurs the input) is handled before the menu closes.
    this.input.addEventListener('blur', () => this.scheduleClose());
  }

  selectedLabel() {
    const option = this.select.selectedOptions[0];
    return option && option.value !== '' ? (option.textContent || '').trim() : '';
  }

  // filter '' (focus/click) always shows the full list — the key difference from the shared widget.
  open(filter = '') {
    const query = filter.trim().toLowerCase();
    this.rendered = query
      ? this.options.filter((option) => option.label.toLowerCase().includes(query))
      : this.options;
    this.renderMenu();
    this.menu.classList.remove('autocomplete__menu--hidden');
    this.placeMenu();
  }

  // Open upward when the (now-visible) menu doesn't fit below the input and there's more room above.
  // CSS keys the actual offset + border radius off data-placement.
  placeMenu() {
    const rect = this.input.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = this.menu.offsetHeight > spaceBelow && spaceAbove > spaceBelow;
    this.menu.dataset.placement = openUp ? 'top' : 'bottom';
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.close(), 120);
  }

  cancelClose() {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  close() {
    this.cancelClose();
    this.menu.classList.add('autocomplete__menu--hidden');
    this.input.value = this.selectedLabel(); // discard any un-committed typing
    this.activeIndex = -1;
  }

  renderMenu() {
    this.menu.innerHTML = '';
    this.activeIndex = -1;
    this.rendered.forEach((option) => {
      const item = document.createElement('li');
      item.className = 'autocomplete__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.textContent = option.label;
      // mousedown, not click: fires before the input's blur, so selection isn't lost to close().
      item.addEventListener('mousedown', (event) => {
        event.preventDefault();
        this.choose(option);
      });
      this.menu.append(item);
    });
  }

  choose(option) {
    const changed = this.select.value !== option.value;
    this.select.value = option.value;
    this.input.value = option.label;
    this.menu.classList.add('autocomplete__menu--hidden');
    this.activeIndex = -1;
    this.cancelClose();
    // Only when the value truly changes — a redundant change would autosave + re-render downstream.
    if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.input.blur();
    this.cancelClose();
  }

  onKeydown(event) {
    if (this.menu.classList.contains('autocomplete__menu--hidden') && event.key !== 'Escape') {
      this.open(this.input.value);
    }
    const items = this.rendered || [];
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (items.length) this.choose(items[this.activeIndex >= 0 ? this.activeIndex : 0]);
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  }

  move(delta) {
    const items = Array.from(this.menu.children);
    if (!items.length) return;
    if (this.activeIndex >= 0) items[this.activeIndex].classList.remove('autocomplete__option--focused');
    this.activeIndex = (this.activeIndex + delta + items.length) % items.length;
    const active = items[this.activeIndex];
    active.classList.add('autocomplete__option--focused');
    active.scrollIntoView({ block: 'nearest' });
  }
}
